import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, PackageOpen, Trash2, Send, FileEdit } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow, SectionHeading } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { vendorsService } from '@/services/vendors';
import {
  inventoryService,
  warehousesService,
} from '@/services/inventory';
import {
  purchaseOrdersService,
  type CreatePurchaseOrderPayload,
} from '@/services/purchase-orders';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import { pickerLabel } from '@/lib/inventory-label';
import type { InventoryItem } from '@/types/inventory';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DraftLine {
  key: number;
  itemId: string;
  /** Quantity in BASE units (no UoM picker on PO yet — keeps the form tight). */
  quantity: string;
  unitPrice: string;
}

let LINE_KEY = 1;
const newLine = (): DraftLine => ({
  key: LINE_KEY++,
  itemId: '',
  quantity: '',
  unitPrice: '',
});

export function PurchaseOrderFormSheet({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [vendorId, setVendorId] = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [externalRef, setExternalRef] = useState('');
  const [eta, setEta] = useState('');
  const [notes, setNotes] = useState('');
  const [issueImmediately, setIssueImmediately] = useState(true);
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);

  const vendorsQuery = useQuery({
    queryKey: ['vendors', { forSelect: true }],
    queryFn: () => vendorsService.list({ status: 'ACTIVE', pageSize: 200 }),
    enabled: open,
    staleTime: 60_000,
  });
  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { status: 'ACTIVE', forPo: true, pickable: true }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', pageSize: 200, pickable: true }),
    enabled: open,
    staleTime: 30_000,
  });
  const warehousesQuery = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => warehousesService.list(),
    enabled: open,
    staleTime: 60_000,
  });

  const vendors = vendorsQuery.data?.items ?? [];
  const items = itemsQuery.data?.items ?? [];
  const warehouses = warehousesQuery.data ?? [];
  const itemById = useMemo(() => {
    const m = new Map<string, InventoryItem>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  // POs procure raw materials from vendors — finished goods are produced,
  // not bought. Show only RAW_MATERIAL items (and never bundles, which the
  // BE rejects). Mirrors the Purchase Request picker.
  const eligibleItems = items.filter((i) => !i.isBundle && i.kind === 'RAW_MATERIAL');

  useEffect(() => {
    if (!open) return;
    setVendorId('');
    setWarehouseId('');
    setExternalRef('');
    setEta('');
    setNotes('');
    setIssueImmediately(true);
    setLines([newLine()]);
  }, [open]);

  const totals = useMemo(() => {
    let lineCount = 0;
    let estTotal = 0;
    let hasUnpriced = false;
    for (const line of lines) {
      const qty = Number(line.quantity);
      if (!line.itemId || !Number.isFinite(qty) || qty <= 0) continue;
      lineCount += 1;
      const explicit = line.unitPrice ? Number(line.unitPrice) : NaN;
      const item = itemById.get(line.itemId);
      const price = Number.isFinite(explicit) ? explicit : (item?.costPrice ?? null);
      if (price == null) hasUnpriced = true;
      else estTotal += qty * price;
    }
    return { lineCount, estTotal, hasUnpriced };
  }, [lines, itemById]);

  const mutation = useMutation({
    mutationFn: () => {
      const built: CreatePurchaseOrderPayload['items'] = lines
        .map((l) => ({
          itemId: l.itemId,
          quantity: Number(l.quantity),
          unitPrice: l.unitPrice ? Number(l.unitPrice) : undefined,
        }))
        .filter((l) => l.itemId && l.quantity > 0);

      if (built.length === 0) throw new Error('Add at least one line with quantity > 0.');

      const payload: CreatePurchaseOrderPayload = {
        vendorId,
        warehouseId,
        items: built,
        externalPoRef: externalRef.trim() || undefined,
        expectedDeliveryAt: eta ? new Date(eta).toISOString() : undefined,
        notes: notes.trim() || undefined,
        issueImmediately,
      };
      return purchaseOrdersService.create(payload);
    },
    onSuccess: (po) => {
      toast.success(`PO ${po.poNumber} created`);
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not create PO'),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!vendorId || !warehouseId) return;
    mutation.mutate();
  };

  const addLine = () => setLines((ls) => [...ls, newLine()]);
  const removeLine = (key: number) =>
    setLines((ls) => (ls.length === 1 ? ls : ls.filter((l) => l.key !== key)));

  const valid = vendorId && warehouseId && totals.lineCount > 0;
  const selectedVendor = vendors.find((v) => v.id === vendorId) ?? null;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <PackageOpen className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>New purchase order</DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              Issue a PO to a vendor
            </DialogTitle>
            {selectedVendor ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                <Badge variant="muted" className="text-[10px]">
                  {selectedVendor.name}
                </Badge>
                <Badge variant="muted" className="font-mono text-[10px]">
                  {selectedVendor.code}
                </Badge>
                {selectedVendor.gstin && (
                  <Badge variant="muted" className="font-mono text-[10px]">
                    GSTIN
                  </Badge>
                )}
              </div>
            ) : (
              <DialogDescription className="text-[12.5px] leading-relaxed">
                Pick the vendor and destination warehouse, add line items. Issue immediately to send
                to the vendor, or save as draft to keep editing.
              </DialogDescription>
            )}
          </div>
        </div>
      }
      title=""
      submitLabel={issueImmediately ? 'Create + issue PO' : 'Save as draft'}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="5xl"
      fixedHeight="xl"
    >
      <div className="space-y-6">
        <section className="space-y-3">
          <SectionHeading title="PO header" />
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-6">
              <FormField label="Vendor" htmlFor="vendor" required>
                <Select value={vendorId} onValueChange={setVendorId}>
                  <SelectTrigger id="vendor" className="h-9">
                    <SelectValue placeholder={vendorsQuery.isLoading ? 'Loading…' : 'Select vendor'} />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        <span>{v.name}</span>
                        <span className="ml-2 font-mono text-[10.5px] text-muted-foreground">
                          {v.code}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <div className="col-span-3">
              <FormField label="Destination warehouse" htmlFor="warehouse" required>
                <Select value={warehouseId} onValueChange={setWarehouseId}>
                  <SelectTrigger id="warehouse" className="h-9">
                    <SelectValue placeholder={warehousesQuery.isLoading ? 'Loading…' : 'Select'} />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        <span className="font-mono text-[10.5px] text-muted-foreground">{w.code}</span>
                        <span className="ml-2">{w.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <div className="col-span-3">
              <FormField label="Expected delivery" htmlFor="eta">
                <Input
                  id="eta"
                  type="date"
                  value={eta}
                  onChange={(e) => setEta(e.target.value)}
                  className="h-9"
                />
              </FormField>
            </div>
            <div className="col-span-12">
              <FormField label="Vendor's quote / external ref" htmlFor="externalRef">
                <Input
                  id="externalRef"
                  value={externalRef}
                  onChange={(e) => setExternalRef(e.target.value)}
                  placeholder="QUO-2026-A14"
                  className="h-9"
                />
              </FormField>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <SectionHeading
            title="Line items"
            hint={`${totals.lineCount} valid · est. ${fmt.inr(totals.estTotal)}${
              totals.hasUnpriced ? ' + unpriced lines' : ''
            }`}
          />
          {eligibleItems.length === 0 && !itemsQuery.isLoading && (
            <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
              No raw-material items yet. Add an item and set its Classification to
              "Raw material" in Inventory first — POs procure raw materials.
            </div>
          )}
          <div className="space-y-2">
            {lines.map((l) => {
              const item = l.itemId ? itemById.get(l.itemId) ?? null : null;
              return (
                <div
                  key={l.key}
                  className="grid grid-cols-12 gap-2 rounded-md border border-border/70 bg-card p-2"
                >
                  <div className="col-span-6">
                    <Select
                      value={l.itemId}
                      onValueChange={(v) =>
                        setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, itemId: v } : x)))
                      }
                    >
                      <SelectTrigger className="h-8 text-[12.5px]">
                        <SelectValue placeholder={itemsQuery.isLoading ? 'Loading SKUs…' : 'Select SKU'} />
                      </SelectTrigger>
                      <SelectContent>
                        {eligibleItems.map((it) => (
                          <SelectItem key={it.id} value={it.id}>
                            <span className="font-mono text-[11px] text-muted-foreground">{it.sku}</span>
                            <span className="ml-2">{pickerLabel(it)}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.001"
                      value={l.quantity}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((x) => (x.key === l.key ? { ...x, quantity: e.target.value } : x)),
                        )
                      }
                      placeholder="Qty"
                      className="h-8 text-right text-[12.5px] tabular-nums"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      value={l.unitPrice}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((x) =>
                            x.key === l.key ? { ...x, unitPrice: e.target.value } : x,
                          ),
                        )
                      }
                      placeholder={item?.costPrice != null ? `${item.costPrice}` : 'Unit ₹'}
                      className="h-8 text-right text-[12.5px] tabular-nums"
                    />
                  </div>
                  <div className="col-span-1 flex items-center text-right text-[11.5px] tabular-nums text-muted-foreground">
                    {l.unitPrice && l.quantity
                      ? fmt.inr(Number(l.unitPrice) * Number(l.quantity))
                      : '—'}
                  </div>
                  <div className="col-span-1 flex items-center justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLine(l.key)}
                      disabled={lines.length === 1}
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addLine}>
            <Plus className="h-3.5 w-3.5" />
            Add line
          </Button>
        </section>

        <section className="grid grid-cols-12 gap-4">
          <div className="col-span-8">
            <FormField label="Notes" htmlFor="notes" hint="Optional — appears in the audit trail.">
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Vendor delivery instructions, quality requirements, payment terms…"
                rows={2}
              />
            </FormField>
          </div>
          <div className="col-span-4 flex items-end">
            <button
              type="button"
              onClick={() => setIssueImmediately((v) => !v)}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-md border bg-card px-3 py-2.5 text-left text-[12.5px] transition-colors',
                issueImmediately
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70 hover:border-border',
              )}
            >
              {issueImmediately ? (
                <Send className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <FileEdit className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <div className="flex-1">
                <div className="font-medium">
                  {issueImmediately ? 'Issue immediately' : 'Save as draft'}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {issueImmediately
                    ? 'Status will be ISSUED on create'
                    : 'Status stays DRAFT until you send'}
                </div>
              </div>
            </button>
          </div>
        </section>

        {!valid && (
          <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
            Pick a vendor, a warehouse, and at least one line with quantity &gt; 0.
          </div>
        )}
      </div>
    </FormSheet>
  );
}
